import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Calendar, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import EventsListing from './EventsListing';

/**
 * Standalone landing page for a single event category.
 * Used by /camps (Master QR Hub) and /tournaments.
 * Designed mobile-first since traffic is heavily QR-scan driven.
 *
 * Props:
 *  - category: "camp" | "tournament"
 *  - title: page H1
 *  - tagline: short value-prop subtitle
 *  - icon: lucide-react icon component
 *  - emptyMessage: shown when no events are published
 *  - accentClass: tailwind text color class for the hero icon
 */
export default function EventsHubPage({ category, title, tagline, icon: Icon, emptyMessage, accentClass = 'text-primary' }) {
  const navigate = useNavigate();
  const dataTestId = `events-hub-${category}`;

  return (
    <div className="min-h-screen bg-background" data-testid={dataTestId}>
      {/* Header */}
      <div className="bg-charcoal border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-primary"
            data-testid="events-hub-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Home
          </Button>
          <div className="text-right">
            <p className="text-sm font-display text-primary">SCC Football Academy</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Icon className={`h-8 w-8 ${accentClass}`} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-gradient-gold mb-3">
            {title}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm sm:text-base">
            {tagline}
          </p>
        </motion.div>

        {/* Listing */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <EventsListing category={category} emptyMessage={emptyMessage} />
        </motion.div>

        {/* Footer help */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground bg-charcoal border border-border rounded-full px-4 py-2">
            <MessageCircle className="h-4 w-4 text-primary" />
            <span>
              Questions?{' '}
              <a href="mailto:academy@sccfootball.com" className="text-primary hover:underline">
                academy@sccfootball.com
              </a>
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* === Pre-configured pages === */

export function CampsHubPage() {
  return (
    <EventsHubPage
      category="camp"
      icon={Calendar}
      title="Holiday Camps & Clinics"
      tagline="Brazilian-style training, professional coaching, and a whole lot of football. Pick a camp to register your child."
      emptyMessage="No camps scheduled right now. Check back soon — we usually announce holiday camps 4-6 weeks ahead."
      accentClass="text-primary"
    />
  );
}

export function TournamentsHubPage() {
  return (
    <EventsHubPage
      category="tournament"
      icon={Trophy}
      title="Tournaments & Competitions"
      tagline="Compete against the best youth academies in Singapore. Browse upcoming tournaments and register your player."
      emptyMessage="No tournaments scheduled right now. Speak to your coach about upcoming league fixtures and competitive events."
      accentClass="text-blue-400"
    />
  );
}
